import Delete from '@mdi/svg/svg/delete.svg?react';
import Plus from '@mdi/svg/svg/plus.svg?react';
import {
  MDIIcon,
  MDIIconButton,
  PopoverTooltip,
  TextArea,
  TextField,
  usePopoverTooltip,
} from '@solinkcorp/solink-components';
import {
  MAX_STATE_DESCRIPTION_LENGTH,
  MAX_STATE_NAME_LENGTH,
  MAX_STATES,
  MIN_STATES,
  type SpotCheckState,
  type UseSpotCheckForm,
} from 'components/SpotCheckPanel/hooks/useSpotCheckForm';
import useLocale from 'src/hooks/useLocale';
import { getLocalizedStr } from 'utils/LocaleUtil';
import { getValidationErrorMessage } from 'utils/yupUtils';
import styles from './StatesList.module.sass';

interface StatesListProps {
  spotCheckForm: UseSpotCheckForm;
}

const STATE_NAME_USER_INPUT_TOLERANCE = 10;
const STATE_DESCRIPTION_USER_INPUT_TOLERANCE = 20;

export default function StatesList(props: StatesListProps) {
  const { spotCheckForm } = props;
  const {
    aiChatAnswerStates,
    addAnswerState,
    updateAnswerState,
    removeAnswerState,
    spotCheckValidationError,
    livePreviewValidationError,
    eventLivePreviewValidationError,
  } = spotCheckForm;

  const locale = useLocale();

  // Get the appropriate validation error based on the mode
  const validationError =
    spotCheckValidationError || livePreviewValidationError || eventLivePreviewValidationError;

  const handleNameChange = (id: string, value: string) => {
    const truncatedValue = value.slice(0, MAX_STATE_NAME_LENGTH + STATE_NAME_USER_INPUT_TOLERANCE);
    updateAnswerState(id, { name: truncatedValue });
  };

  const handleDescriptionChange = (id: string, value: string) => {
    const truncatedValue = value.slice(
      0,
      MAX_STATE_DESCRIPTION_LENGTH + STATE_DESCRIPTION_USER_INPUT_TOLERANCE,
    );
    updateAnswerState(id, { description: truncatedValue });
  };

  const handleRemoveAnswerState = (id: string, e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    removeAnswerState(id);
  };

  const canAddAnswerState = aiChatAnswerStates.length < MAX_STATES;
  const canRemoveAnswerState = aiChatAnswerStates.length > MIN_STATES;

  const addResponseTooltip = usePopoverTooltip({ placement: 'top' });

  return (
    <div className={styles.statesListContainer}>
      <div className={styles.headerSection}>
        <label className={`${styles.statesListLabel} states-list-label`}>
          {locale.scheduledAIChat.statesLabel}
        </label>
        <div className={styles.statesListDescription}>
          {locale.scheduledAIChat.statesDescription}
        </div>
      </div>
      {aiChatAnswerStates.map((state: SpotCheckState, index: number) => {
        const nameError = getValidationErrorMessage(
          validationError,
          `aiChatAnswerStates[${index}].name`,
        );
        const descriptionError = getValidationErrorMessage(
          validationError,
          `aiChatAnswerStates[${index}].description`,
        );

        const nameLabel = index === 0 ? `${locale.scheduledAIChat.stateNameLabel} *` : undefined;
        const descriptionLabel =
          index === 0 ? `${locale.scheduledAIChat.stateDescriptionLabel} *` : undefined;
        const namePlaceholder = index === 0 ? undefined : locale.scheduledAIChat.stateNameLabel;
        const descriptionPlaceholder =
          index === 0 ? undefined : locale.scheduledAIChat.stateDescriptionLabel;

        return (
          <div key={state.id} className={styles.stateItem}>
            <div className={styles.nameFieldContainer}>
              <TextField
                labelText={nameLabel}
                value={state.name}
                onChange={({ value }: { value: string }) => handleNameChange(state.id, value)}
                name={`state-name-${state.id}`}
                placeholder={namePlaceholder}
                errorText={nameError}
                maxLength={MAX_STATE_NAME_LENGTH + STATE_NAME_USER_INPUT_TOLERANCE}
              />
            </div>
            <div className={styles.descriptionFieldContainer}>
              <TextArea
                labelText={descriptionLabel}
                value={state.description}
                onChange={({ value }: { value: string }) =>
                  handleDescriptionChange(state.id, value)
                }
                name={`state-description-${state.id}`}
                rows={1}
                autoExpandTextarea={true}
                placeholder={descriptionPlaceholder}
                errorText={descriptionError}
                maxLength={MAX_STATE_DESCRIPTION_LENGTH + STATE_DESCRIPTION_USER_INPUT_TOLERANCE}
              />
            </div>
            <div
              className={`${styles.actionsContainer} ${
                index === 0 ? styles.firstStateActions : ''
              }`}
            >
              <MDIIconButton
                title={locale.general?.remove || 'Remove'}
                icon={Delete}
                fill={
                  !canRemoveAnswerState ? 'var(--color-dark-gray-lightest)' : 'var(--color-red)'
                }
                onClick={(e) => handleRemoveAnswerState(state.id, e)}
                disabled={!canRemoveAnswerState}
              />
            </div>
          </div>
        );
      })}
      <>
        <button
          type="button"
          onClick={addAnswerState}
          className={styles.addResponseLink}
          disabled={!canAddAnswerState}
          ref={!canAddAnswerState ? addResponseTooltip.setTriggerRef : undefined}
        >
          <MDIIcon icon={Plus} size={20} />
          <span>{locale.scheduledAIChat.addResponse}</span>
        </button>
        {!canAddAnswerState && (
          <PopoverTooltip {...addResponseTooltip}>
            {getLocalizedStr({
              string: locale.scheduledAIChat.addResponseMaxReached,
              replacements: { maxStates: MAX_STATES },
            })}
          </PopoverTooltip>
        )}
      </>
    </div>
  );
}
